import React, { useState, useEffect } from 'react';
import './SummaryMenu.css';

const SummaryMenu = ({ transcriptId, onClose, onSeekToTime }) => {
  const [summary, setSummary] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [showTimestamps, setShowTimestamps] = useState(true);
  const [cached, setCached] = useState(false);

  useEffect(() => {
    if (transcriptId) {
      generateSummary();
    }
  }, [transcriptId]);

  const generateSummary = async (forceRegenerate = false) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`/api/transcripts/${transcriptId}/generate-summary`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          summaryType: 'detailed',
          useCache: !forceRegenerate
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || 'Failed to generate summary');
      }

      const data = await response.json();
      setSummary(data.summary);
      setCached(data.cached || false);
    } catch (err) {
      console.error('Error generating summary:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSeekToTime = (timestamp) => {
    if (onSeekToTime && timestamp !== undefined) {
      onSeekToTime(timestamp);
    }
  };

  const renderSummary = () => {
    if (!summary) return null;

    // If summary is a structured object with timestamps
    if (typeof summary === 'object' && summary.type === 'structured') {
      return (
        <div className="summary-content structured">
          {summary.paragraphs.map((para, index) => (
            <div key={index} className="summary-paragraph">
              {showTimestamps && para.formattedTime && (
                <button
                  className="summary-timestamp"
                  onClick={() => handleSeekToTime(para.timestamp)}
                  title="Jump to this time in the transcript"
                >
                  {para.formattedTime}
                </button>
              )}
              <p className="summary-text">{para.text}</p>
            </div>
          ))}
        </div>
      );
    }

    // Plain text summary
    return (
      <div className="summary-content plain">
        <p>{summary}</p>
      </div>
    );
  };

  return (
    <div className="summary-menu-overlay" onClick={onClose}>
      <div className="summary-menu" onClick={(e) => e.stopPropagation()}>
        <div className="summary-header">
          <h2>AI Summary</h2>
          <div className="summary-controls">
            {summary && typeof summary === 'object' && summary.type === 'structured' && (
              <button
                className={`control-btn ${showTimestamps ? 'active' : ''}`}
                onClick={() => setShowTimestamps(!showTimestamps)}
                title="Toggle timestamps"
              >
                {showTimestamps ? '🕐 Hide Times' : '🕐 Show Times'}
              </button>
            )}
            {summary && (
              <button
                className="control-btn"
                onClick={() => generateSummary(true)}
                disabled={isLoading}
                title="Regenerate summary"
              >
                🔄 Regenerate
              </button>
            )}
            <button className="close-btn" onClick={onClose} title="Close">
              ✕
            </button>
          </div>
        </div>

        <div className="summary-body">
          {isLoading && (
            <div className="loading-state">
              <div className="spinner"></div>
              <p>Generating AI summary...</p>
              <small>This may take a moment for longer transcripts</small>
            </div>
          )}

          {error && (
            <div className="error-state">
              <p className="error-message">⚠️ {error}</p>
              <button className="retry-btn" onClick={() => generateSummary()}>
                Try Again
              </button>
            </div>
          )}

          {!isLoading && !error && summary && (
            <>
              {cached && (
                <div className="cached-notice">
                  <small>✓ Cached summary (generated previously)</small>
                </div>
              )}
              {renderSummary()}
            </>
          )}
        </div>

        <div className="summary-footer">
          <small className="summary-info">
            AI-generated summary may not capture all details. Review the full transcript for accuracy.
          </small>
        </div>
      </div>
    </div>
  );
};

export default SummaryMenu;
